/* =============================================================
   RONCASA — LANDED PROPERTY DASHBOARD
   Filter state, table rendering, map, section-nav, accordions.
   Loaded once by landed-hero.liquid (first section on page).

   Data sources (rendered server-side by their owning section as
   <script type="application/json"> tags, real BigQuery aggregates —
   see scripts/content/landed_dashboard_refresh.py in roncasa-scripts):
     #ld-district-data  — landed-data-table.liquid (page.metafields.landed.district_data)
     #ld-area-data      — landed-data-table.liquid (page.metafields.landed.area_data)
     #ld-area-meta      — landed-map.liquid         (page.metafields.landed.area_meta)

   Ported from design_handoff_landed_dashboard/landed-dashboard.js.
   Deliberate departures from that mockup (real data has no fabricated
   per-row sample transactions to drive a modal, and no synthetic
   basePsf to blend across a multi-select filter — see the refresh
   script's own module docstring for the full reasoning):
     - No modal / openModal() / sampleTx() — rows are informational,
       not clickable.
     - No client-side statsFor()/combinedMult() blending. Each row in
       district_data/area_data is already a real, independently-computed
       BigQuery aggregate for one (area, type, tenure, band) combo, or
       for the 'All Types x All Tenures' combo when every chip is
       selected (the default). Selecting a narrower type/tenure subset
       simply filters down to the matching real combo rows instead of
       inventing a blended number — see renderTable()'s comment below.
     - Cells with tx_count < 5 are never sent to the browser at all
       (the refresh script drops them server-side), so "no matching
       row" already means "insufficient sample" — no separate n<5 UI
       branch is needed here, only an empty-state message.
   ============================================================= */
(function () {
  function readJSON(id) {
    var el = document.getElementById(id);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  var DISTRICT_DATA = readJSON('ld-district-data') || [];
  var AREA_DATA = readJSON('ld-area-data') || [];
  var AREA_META = readJSON('ld-area-meta') || [];
  var AREA_TRANSACTIONS = readJSON('ld-area-transactions') || {};

  // Static reference content — matches design_handoff_landed_dashboard/
  // landed-data.js sizeBands verbatim (also rendered server-side, static,
  // in landed-size-guide.liquid). Used here only for the filter bar's
  // band-info strip copy.
  var SIZE_BANDS = [
    { id: 'A', name: 'Entry / Micro-Landed', range: 'Under 1,000 sqft', gets: 'Compact Type II intermediate or corner terraces, built up (3 storeys + attic) to maximise space.' },
    { id: 'B', name: 'Compact Terrace', range: '1,000 – 1,500 sqft', gets: 'Larger Type II terraces, or older un-renovated Type I terraces.' },
    { id: 'C', name: 'Standard Core', range: '1,500 – 2,200 sqft', gets: 'Standard Type I intermediate terraces and average corner terraces.' },
    { id: 'D', name: 'Premium Land', range: '2,200 – 3,500 sqft', gets: 'Large corner terraces and standard semi-detached homes.' },
    { id: 'E', name: 'Luxury Estate', range: '3,500 – 5,000 sqft', gets: 'Massive semi-detached houses or entry-level detached bungalows.' },
    { id: 'F', name: 'Ultra-Prime', range: 'Above 5,000 sqft', gets: 'Grand bungalows, spanning up to GCBs (from 15,069 sqft).' },
  ];
  var BAND_BY_ID = {};
  SIZE_BANDS.forEach(function (b) { BAND_BY_ID[b.id] = b; });

  var ALL_TYPES = ['Terrace', 'Semi-Detached', 'Detached'];
  var ALL_TENURES = ['Non-Strata', 'Strata'];

  // Region rotation order (District scope only — added 8 Aug 2026, Ronnie's
  // review feedback: the flat 24-district list was "very long to scroll"
  // for a quick read, especially on a phone or for less tech-savvy
  // visitors). OCR first (most landed stock lives there), then RCR, CCR.
  var REGIONS = ['OCR', 'RCR', 'CCR'];
  var ROTATE_INTERVAL_MS = 5000;
  var rotateTimer = null;

  var state = {
    scope: 'district',
    mode: 'all', // 'all' | 'rotate' — District scope only
    regionIdx: 0,
    types: new Set(ALL_TYPES),
    tenure: new Set(ALL_TENURES),
    band: 'all',
  };

  function fmtPsf(n) { return n == null ? '—' : '$' + n.toLocaleString(); }
  function fmtQ(n) {
    if (n == null) return '—';
    if (n >= 1e6) return '$' + (n / 1e6).toFixed(n >= 1e7 ? 1 : 2) + 'M';
    return '$' + Math.round(n / 1000) + 'K';
  }

  /* ---------- FILTER BAR ---------- */
  function renderFilterUI() {
    var scopeSeg = document.getElementById('ldScopeSeg');
    if (scopeSeg) scopeSeg.querySelectorAll('button').forEach(function (b) { b.classList.toggle('on', b.dataset.v === state.scope); });

    // "District list" mode toggle (All / Region flip) only makes sense for
    // District scope — Area rows don't carry a region tag.
    var modeGroup = document.getElementById('ldModeGroup');
    if (modeGroup) modeGroup.style.display = state.scope === 'district' ? '' : 'none';
    var modeSeg = document.getElementById('ldModeSeg');
    if (modeSeg) modeSeg.querySelectorAll('button').forEach(function (b) { b.classList.toggle('on', b.dataset.v === state.mode); });

    var typeChips = document.getElementById('ldTypeChips');
    if (typeChips) typeChips.querySelectorAll('.ld-chip').forEach(function (b) { b.classList.toggle('on', state.types.has(b.dataset.v)); });
    var tenureChips = document.getElementById('ldTenureChips');
    if (tenureChips) tenureChips.querySelectorAll('.ld-chip').forEach(function (b) { b.classList.toggle('on', state.tenure.has(b.dataset.v)); });
    var bandSeg = document.getElementById('ldBandSeg');
    if (bandSeg) bandSeg.querySelectorAll('button').forEach(function (b) { b.classList.toggle('on', b.dataset.v === state.band); });

    var info = document.getElementById('ldBandInfo');
    if (!info) return;
    if (state.band === 'all') { info.classList.remove('on'); info.innerHTML = ''; return; }
    var b = BAND_BY_ID[state.band];
    if (!b) { info.classList.remove('on'); return; }
    info.classList.add('on');
    info.innerHTML = '<span class="ld-bi-tag">Band ' + b.id + '</span><span class="ld-bi-name">' + b.name + '</span>' +
      '<span class="ld-bi-rng">' + b.range + '</span><p>' + b.gets + '</p>';
  }

  /* ---------- TABLE ----------
     One row per real (area, type, tenure, band) combo — see file header.
     Default filter state (every type + every tenure selected) matches
     against the 'All Types' / 'All Tenures' aggregate rows the refresh
     script computes as a fresh quantile over the full population. A
     narrowed selection matches the specific per-combo rows instead —
     multiple honest rows per area rather than one blended one. */
  function matchingRows() {
    var src = state.scope === 'area' ? AREA_DATA : DISTRICT_DATA;
    var wantAllTypes = state.types.size === ALL_TYPES.length;
    var wantAllTenure = state.tenure.size === ALL_TENURES.length;
    var rows = src.filter(function (r) {
      if (r.band !== state.band) return false;
      if (wantAllTypes && wantAllTenure) {
        return r.type === 'All Types' && r.tenure === 'All Tenures';
      }
      if (r.type === 'All Types' || r.tenure === 'All Tenures') return false;
      return state.types.has(r.type) && state.tenure.has(r.tenure);
    });
    if (state.scope === 'district' && state.mode === 'rotate') {
      var region = REGIONS[state.regionIdx];
      rows = rows.filter(function (r) { return r.region === region; });
    }
    return rows;
  }

  /* ---------- REGION ROTATION (District scope, "Region flip" mode) ---------- */
  function updateRegionIndicator() {
    var el = document.getElementById('ldRegionIndicator');
    if (!el) return;
    if (state.scope !== 'district' || state.mode !== 'rotate') {
      el.style.display = 'none';
      return;
    }
    el.style.display = '';
    el.textContent = 'Now showing: ' + REGIONS[state.regionIdx] + ' districts (' + (state.regionIdx + 1) + ' of ' + REGIONS.length + ') — cycles every 5s';
  }

  function stopRotation() {
    if (rotateTimer) { clearInterval(rotateTimer); rotateTimer = null; }
  }

  function startRotation() {
    stopRotation();
    rotateTimer = setInterval(function () {
      state.regionIdx = (state.regionIdx + 1) % REGIONS.length;
      updateRegionIndicator();
      renderTable();
    }, ROTATE_INTERVAL_MS);
  }

  function renderTable() {
    var wrap = document.getElementById('ldTblRows');
    if (!wrap) return;
    var rows = matchingRows().slice().sort(function (a, b) { return (b.median_psf || 0) - (a.median_psf || 0); });

    var countEl = document.getElementById('ldRowCount');
    if (countEl) countEl.textContent = rows.length + ' row' + (rows.length === 1 ? '' : 's');

    if (!rows.length) {
      var emptyMsg = (state.scope === 'district' && state.mode === 'rotate')
        ? 'No ' + REGIONS[state.regionIdx] + ' districts clear the minimum sample size for this filter combination.'
        : 'No areas clear the minimum sample size (5 transactions) for this filter combination. Try a broader band or house type.';
      wrap.innerHTML = '<div class="ld-tbl-empty">' + emptyMsg + '</div>';
      return;
    }

    wrap.innerHTML = rows.map(function (r) {
      var showCombo = !(r.type === 'All Types' && r.tenure === 'All Tenures');
      var sub, tierHtml;
      var windowTag = r.window ? (' · ' + r.window) : '';
      if (state.scope === 'area') {
        var tierClass = r.tier === 'GCBA' ? 'gcba' : 'subzone';
        tierHtml = '<span class="ld-tier-tag ' + tierClass + '">' + r.tier + '</span>';
        sub = (showCombo ? (r.district + ' · ' + r.type + ' · ' + r.tenure) : r.district) + windowTag;
      } else {
        tierHtml = '<span class="ld-tier-tag district">' + (r.region || 'District') + '</span>';
        sub = (showCombo ? (r.type + ' · ' + r.tenure) : 'All house types · all tenure') + windowTag;
      }
      // Quantum column shows the median bold, low-high range small beneath
      // it — same low/median/high idea as the PSF columns, just stacked
      // instead of three separate columns (added 8 Aug 2026 per Ronnie).
      var quantumHtml = '<div class="ld-qtm-med">' + fmtQ(r.median_quantum) + '</div>';
      if (r.low_quantum != null && r.high_quantum != null) {
        quantumHtml += '<div class="ld-qtm-rng">' + fmtQ(r.low_quantum) + ' – ' + fmtQ(r.high_quantum) + '</div>';
      }
      return '<div class="ld-tbl-row">'
        + '<div><div class="ld-name">' + r.area + '</div><div class="ld-sub">' + sub + '</div></div>'
        + '<div>' + tierHtml + '</div>'
        + '<div class="ld-num dim" style="text-align:right">' + fmtPsf(r.low_psf) + '</div>'
        + '<div class="ld-num" style="text-align:right">' + fmtPsf(r.median_psf) + '</div>'
        + '<div class="ld-num dim" style="text-align:right">' + fmtPsf(r.high_psf) + '</div>'
        + '<div class="ld-qtm" style="text-align:right">' + quantumHtml + '</div>'
        + '<div class="ld-qtm" style="text-align:right">' + r.tx_count + '</div>'
        + '</div>';
    }).join('');
  }

  /* ---------- ACCORDION (Reference section) ---------- */
  function initAccordion() {
    document.querySelectorAll('.ld-acc-head').forEach(function (h) {
      h.addEventListener('click', function () {
        var body = document.getElementById(h.dataset.target);
        if (!body) return;
        var open = body.classList.toggle('open');
        h.classList.toggle('open', open);
      });
    });
  }

  /* ---------- MAP ---------- */
  var mapInited = false;
  function findAreaDefaultStats(areaName) {
    return AREA_DATA.find(function (r) {
      return r.area === areaName && r.type === 'All Types' && r.tenure === 'All Tenures' && r.band === 'all';
    });
  }

  function renderMapSide(area) {
    var el = document.getElementById('ldMapSide');
    if (!el) return;
    var s = findAreaDefaultStats(area.area);
    var body = '<div class="ld-ms-title">Selected area</div><div class="ld-ms-card">'
      + '<h4>' + area.area + '</h4><div class="ld-subline">' + (area.planning_area || '') + ' · ' + area.tier + '</div>';
    if (s) {
      body += '<div class="ld-ms-stat-row"><span class="ld-l">Median PSF</span><span class="ld-v">' + fmtPsf(s.median_psf) + '</span></div>'
        + '<div class="ld-ms-stat-row"><span class="ld-l">Range</span><span class="ld-v">' + fmtPsf(s.low_psf) + ' – ' + fmtPsf(s.high_psf) + '</span></div>'
        + '<div class="ld-ms-stat-row"><span class="ld-l">Sample (' + (s.window || '—') + ')</span><span class="ld-v">' + s.tx_count + ' tx</span></div>';
    } else {
      body += '<div class="ld-ms-empty">Insufficient sample — fewer than 5 landed transactions on record for this area, even counting the full available history.</div>';
    }
    // Real transaction history (added 8 Aug 2026 per Ronnie) — not a "total
    // landed units in this area" figure, which no source table can back up
    // (see landed_dashboard_refresh.py's fetch_area_transactions() docstring
    // for why that number is deliberately not shown rather than guessed).
    var txns = AREA_TRANSACTIONS[area.area];
    if (txns && txns.length) {
      body += '<div class="ld-ms-tx-head">Transaction history (' + txns.length + (txns.length >= 20 ? '+' : '') + ')</div>'
        + '<div class="ld-ms-tx-list">' + txns.map(function (t) {
          return '<div class="ld-ms-tx"><div class="ld-ms-tx-top"><span>' + t.street + '</span><span>' + fmtQ(t.price) + '</span></div>'
            + '<div class="ld-ms-tx-sub">' + t.date + ' · ' + t.type + ' · ' + t.tenure + ' · ' + fmtPsf(t.psf) + ' psf'
            + (t.area_sqft ? ' · ' + t.area_sqft.toLocaleString() + ' sqft' : '') + '</div></div>';
        }).join('') + '</div>';
    } else {
      body += '<div class="ld-ms-tx-head">Transaction history</div><div class="ld-ms-empty">No individual transactions on record for this area.</div>';
    }
    body += '</div>';
    el.innerHTML = body;
  }

  // area name (as it appears in area_meta) -> its Leaflet marker, so the
  // Reference section's area links can select the right pin (added 8 Aug
  // 2026 per Ronnie: "link them to reflect to the map").
  var AREA_MARKERS = {};

  function selectAreaOnMap(areaName) {
    var marker = AREA_MARKERS[areaName];
    if (!marker || !window.__ldMap) return false;
    window.__ldMap.setView(marker.getLatLng(), 16);
    marker.openTooltip();
    marker.fire('click');
    return true;
  }

  function initMap() {
    if (mapInited || !window.L || !AREA_META.length) return;
    var mapEl = document.getElementById('ld-leaflet-map');
    if (!mapEl) return;
    mapInited = true;
    var map = L.map('ld-leaflet-map', { scrollWheelZoom: false, zoomControl: false });
    window.__ldMap = map;
    L.control.zoom({ position: 'topright' }).addTo(map);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '&copy; OpenStreetMap contributors', maxZoom: 17 }).addTo(map);

    // Real bounding box of the actual geocoded centroids, not a guessed
    // center/zoom — Ronnie flagged the fixed view (8 Aug 2026) as showing
    // more Johor Bahru than Singapore at zoom 11. fitBounds is self-
    // correcting regardless of how the area_meta set changes over time.
    var latLngs = AREA_META.map(function (a) { return [a.lat, a.lng]; });
    var bounds = L.latLngBounds(latLngs);

    AREA_META.forEach(function (a) {
      var isGcba = a.tier === 'GCBA';
      // GCBA = filled solid dot. Subzone = true hollow outline (no fill),
      // not a low-opacity fill — with ~300 pins, semi-transparent fills
      // stack visually into a solid black mass as they overlap, which is
      // what made every pin look GCBA-like at a glance. A real outline
      // stays readable no matter how many overlap.
      var marker = L.circleMarker([a.lat, a.lng], {
        radius: 5,
        color: '#0E0E0E',
        weight: isGcba ? 1 : 1.5,
        opacity: isGcba ? 0.9 : 0.55,
        fillColor: '#0E0E0E',
        fillOpacity: isGcba ? 0.85 : 0,
      }).addTo(map);
      marker.bindTooltip(a.area, { direction: 'top' });
      marker.on('click', function () { renderMapSide(a); });
      AREA_MARKERS[a.area] = marker;
    });

    map.fitBounds(bounds, { padding: [24, 24] });
  }

  /* ---------- SECTION NAV ACTIVE STATE ---------- */
  function initSectNav() {
    var links = document.querySelectorAll('.ld-sect-nav a');
    if (!links.length) return;
    var sections = Array.from(links).map(function (l) { return document.querySelector(l.getAttribute('href')); });
    var lastIdx = -1;
    window.addEventListener('scroll', function () {
      var y = window.scrollY + 180;
      var idx = 0;
      sections.forEach(function (s, i) { if (s && s.offsetTop <= y) idx = i; });
      links.forEach(function (l, i) { l.classList.toggle('on', i === idx); });
      if (idx !== lastIdx) {
        lastIdx = idx;
        var activeLink = links[idx];
        if (activeLink) activeLink.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }, { passive: true });
  }

  /* ---------- INIT ---------- */
  document.addEventListener('DOMContentLoaded', function () {
    renderTable();
    initAccordion();
    initSectNav();

    var scopeSeg = document.getElementById('ldScopeSeg');
    if (scopeSeg) scopeSeg.addEventListener('click', function (e) {
      var b = e.target.closest('button'); if (!b) return;
      state.scope = b.dataset.v;
      if (state.scope !== 'district') { state.mode = 'all'; stopRotation(); }
      renderFilterUI(); updateRegionIndicator(); renderTable();
    });
    var modeSeg = document.getElementById('ldModeSeg');
    if (modeSeg) modeSeg.addEventListener('click', function (e) {
      var b = e.target.closest('button'); if (!b) return;
      state.mode = b.dataset.v;
      state.regionIdx = 0;
      if (state.mode === 'rotate') { startRotation(); } else { stopRotation(); }
      renderFilterUI(); updateRegionIndicator(); renderTable();
    });
    var typeChips = document.getElementById('ldTypeChips');
    if (typeChips) typeChips.addEventListener('click', function (e) {
      var b = e.target.closest('.ld-chip'); if (!b) return;
      var v = b.dataset.v;
      if (state.types.has(v) && state.types.size > 1) state.types.delete(v); else state.types.add(v);
      renderFilterUI(); renderTable();
    });
    var tenureChips = document.getElementById('ldTenureChips');
    if (tenureChips) tenureChips.addEventListener('click', function (e) {
      var b = e.target.closest('.ld-chip'); if (!b) return;
      var v = b.dataset.v;
      if (state.tenure.has(v) && state.tenure.size > 1) state.tenure.delete(v); else state.tenure.add(v);
      renderFilterUI(); renderTable();
    });
    var bandSeg = document.getElementById('ldBandSeg');
    if (bandSeg) bandSeg.addEventListener('click', function (e) {
      var b = e.target.closest('button'); if (!b) return;
      state.band = b.dataset.v; renderFilterUI(); renderTable();
    });

    renderFilterUI();
    updateRegionIndicator();
    initMap();
    setTimeout(function () { if (window.__ldMap) window.__ldMap.invalidateSize(); }, 300);

    // Reference section area names -> jump to + select the matching map pin
    // (8 Aug 2026 per Ronnie: "link them to reflect to the map"). Event
    // delegation on the whole document since both accordions can be
    // re-rendered/toggled independently.
    document.addEventListener('click', function (e) {
      var el = e.target.closest('.ld-area-link');
      if (!el) return;
      var found = selectAreaOnMap(el.dataset.area);
      if (found) {
        var mapSection = document.getElementById('map');
        if (mapSection) mapSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  });
})();
